import { clearSession, readSession } from '../auth/token';

declare global {
  interface Window {
    __APP_CONFIG__?: { apiBaseUrl?: string };
  }
}

/**
 * Where the API lives, in order of precedence:
 *
 *  1. window.__APP_CONFIG__ — written by the container at start-up, so the same
 *     built image works against any API without rebuilding
 *  2. VITE_API_BASE_URL — compiled in, for `npm run dev`
 *  3. localhost, for a checkout with no configuration at all
 */
const BASE_URL: string =
  window.__APP_CONFIG__?.apiBaseUrl ||
  import.meta.env.VITE_API_BASE_URL ||
  'http://localhost:3000/api/v1';

/**
 * Called whenever the API rejects our token, so the shell can send the operator
 * back to the sign-in screen. Registered by the auth provider rather than
 * imported from it, which keeps this module free of React.
 */
let onUnauthorized: (() => void) | null = null;

export function setUnauthorizedHandler(handler: (() => void) | null): void {
  onUnauthorized = handler;
}

export class ApiError extends Error {
  public readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

type QueryValue = string | number | boolean | undefined | null;

function buildUrl(path: string, query?: Record<string, QueryValue>): string {
  // Resolved against the page origin so BASE_URL may be relative. Behind a
  // single-origin proxy the API is reached at "/api/v1", and `new URL()` throws
  // on a relative string unless it is given a base. An absolute BASE_URL still
  // wins — the second argument is ignored when the first one is absolute.
  const url = new URL(`${BASE_URL}${path}`, window.location.origin);

  for (const [key, value] of Object.entries(query ?? {})) {
    if (value !== undefined && value !== null && value !== '') {
      url.searchParams.set(key, String(value));
    }
  }

  return url.toString();
}

async function request<T>(path: string, init: RequestInit, query?: Record<string, QueryValue>): Promise<T> {
  let response: Response;

  try {
    const session = readSession();

    response = await fetch(buildUrl(path, query), {
      ...init,
      headers: {
        Accept: 'application/json',
        ...(session ? { Authorization: `Bearer ${session.token}` } : {}),
        ...init.headers,
      },
    });
  } catch (cause) {
    // A network-level failure is almost always "the API is not running", and
    // saying so is more useful than surfacing the raw TypeError.
    throw new ApiError(0, `Cannot reach the API at ${BASE_URL}. Is the api app started? (${String(cause)})`);
  }

  if (!response.ok) {
    const body = await response.text().catch(() => '');

    // 401 means the token is gone, expired or was signed by a rotated secret.
    // Whatever the cause, the stored one is useless: drop it and let the shell
    // show the sign-in screen instead of every card failing on its own.
    if (response.status === 401) {
      clearSession();
      onUnauthorized?.();
    }

    throw new ApiError(response.status, body || `${response.status} ${response.statusText}`);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

export function get<T>(path: string, query?: Record<string, QueryValue>): Promise<T> {
  return request<T>(path, { method: 'GET' }, query);
}

export function post<T>(path: string, query?: Record<string, QueryValue>): Promise<T> {
  return request<T>(path, { method: 'POST' }, query);
}

/** POST with a JSON body; used by the sign-in form. */
export function postJson<T>(path: string, body: unknown): Promise<T> {
  return request<T>(path, {
    method: 'POST',
    body: JSON.stringify(body),
    headers: { 'Content-Type': 'application/json' },
  });
}

export { BASE_URL };

/**
 * Downloads a protected file.
 *
 * A plain <a href> cannot carry the Authorization header, so the bytes are
 * fetched, wrapped in a blob and handed to a synthetic link. The filename comes
 * from Content-Disposition when the server sends one, so the archive keeps its
 * timestamp.
 */
export async function download(path: string, fallbackName: string): Promise<void> {
  const session = readSession();

  const response = await fetch(buildUrl(path), {
    headers: session ? { Authorization: `Bearer ${session.token}` } : {},
  });

  if (!response.ok) {
    if (response.status === 401) {
      clearSession();
      onUnauthorized?.();
    }

    throw new ApiError(response.status, await response.text().catch(() => ''));
  }

  const disposition = response.headers.get('Content-Disposition') ?? '';
  const match = /filename="([^"]+)"/.exec(disposition);
  const url = URL.createObjectURL(await response.blob());

  const link = document.createElement('a');
  link.href = url;
  link.download = match ? match[1] : fallbackName;
  document.body.appendChild(link);
  link.click();
  link.remove();

  // Revoking immediately can cancel the download in some browsers.
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}

/** Multipart upload; the browser sets the boundary, so no Content-Type here. */
export async function upload<T>(path: string, field: string, file: File): Promise<T> {
  const body = new FormData();
  body.append(field, file);

  return request<T>(path, { method: 'POST', body });
}
